import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOptionalI18n } from '../../i18n/I18nProvider';
import { subscribeOperationsAnnouncements } from './announcementBus';
import './OperationsAnnouncementLayer.css';
const HANGUL_RE = /[가-힣]/;


function isActive(item) {
  const now = Date.now();
  if (!item) return false;
  if (item.startsAt && Date.parse(item.startsAt) > now) return false;
  if (item.endsAt && Date.parse(item.endsAt) <= now) return false;
  return !['DISABLED', 'EXPIRED', 'DELETED'].includes(String(item.status || 'ACTIVE').toUpperCase());
}

function normalizeAnnouncementLanguage(value) {
  let lang = String(value || '').trim().replace(/_/g, '-').toLowerCase();
  if (!lang) return '';
  if (lang === 'kr' || lang.startsWith('ko')) return 'ko';
  if (lang === 'zh-hant' || lang === 'zh-hk' || lang === 'zh-mo') return 'zh-tw';
  if (lang === 'zh-cn' || lang === 'zh-sg' || lang === 'zh-hans') return 'zh';
  if (lang.startsWith('pt-')) return 'pt';
  if (lang.startsWith('es-')) return 'es';
  return lang;
}

function resolveLocalizedAnnouncement(item, currentLanguage) {
  if (!item || typeof item !== 'object') return null;
  const lang = normalizeAnnouncementLanguage(currentLanguage || 'en') || 'en';
  const explicitLanguage = normalizeAnnouncementLanguage(item.language || item.lang || item.locale);
  const dictionaries = [item.translations, item.localized, item.locales, item.i18n]
    .find((value) => value && typeof value === 'object' && !Array.isArray(value));
  const localized = dictionaries
    ? (dictionaries[lang]
      || dictionaries[lang.replace('-', '_')]
      || (lang !== 'en' ? dictionaries.en : null)
      || null)
    : null;

  if (!localized && explicitLanguage && explicitLanguage !== lang && !(lang !== 'en' && explicitLanguage === 'en')) {
    return null;
  }

  const resolved = (!localized || typeof localized !== 'object')
    ? item
    : {
        ...item,
        title: localized.title ?? item.title,
        message: localized.message ?? localized.text ?? item.message,
      };

  // Legacy announcements sometimes arrived as an untagged Korean-only string.
  // Never leak that copy into English/other locales; Korean users still see it.
  if (lang !== 'ko' && HANGUL_RE.test(`${resolved?.title || ''} ${resolved?.message || ''}`)) {
    return null;
  }
  return resolved;
}

function getBannerAnimationSeconds(item) {
  const textLength = `${item?.title || ''} ${item?.message || ''}`.trim().length;
  return Math.min(32, Math.max(12, 10 + (textLength * 0.18)));
}

export default function OperationsAnnouncementLayer() {
  const { t, lang } = useOptionalI18n();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => new Set());

  useEffect(() => subscribeOperationsAnnouncements((announcement) => {
    if (!isActive(announcement)) return;
    setItems((current) => {
      const key = String(announcement.id || '');
      const next = current.filter((item) => String(item.id) !== key);
      next.push(announcement);
      return next.sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0)).slice(0, 20);
    });
  }), []);

  useEffect(() => {
    // No countdown is rendered here, so a 1s global interval is unnecessary. Wake only
    // when the nearest announcement can actually expire; live updates still arrive via
    // subscribeOperationsAnnouncements.
    if (!items.length) return undefined;
    const now = Date.now();
    const nearestEnd = items
      .map((item) => Date.parse(item?.endsAt || ''))
      .filter((ts) => Number.isFinite(ts) && ts > now)
      .sort((a, b) => a - b)[0];
    if (!nearestEnd) return undefined;

    const timer = setTimeout(() => {
      setItems((current) => {
        const next = current.filter(isActive);
        return next.length === current.length ? current : next;
      });
    }, Math.max(50, Math.min(nearestEnd - now + 25, 2147483000)));
    return () => clearTimeout(timer);
  }, [items]);

  const visible = useMemo(() => items
    .map((item) => resolveLocalizedAnnouncement(item, lang))
    .filter((item) => item && !dismissed.has(String(item.id))), [items, dismissed, lang]);
  const banner = visible.find((item) => String(item.channel || '').toUpperCase() === 'BANNER');
  const popup = visible.find((item) => String(item.channel || '').toUpperCase() === 'POPUP');
  const system = visible.find((item) => ['SYSTEM_MESSAGE', 'TOAST'].includes(String(item.channel || '').toUpperCase()));

  const dismiss = (item) => {
    if (item?.dismissible === false) return;
    setDismissed((current) => new Set([...current, String(item.id)]));
  };

  const layer = (
    <>
      {banner ? (
        <div className="npc-operations-banner" role="status" aria-live="polite">
          <div className="npc-operations-banner-viewport">
            <div
              key={`${banner.id || ''}:${banner.title || ''}:${banner.message || ''}`}
              className="npc-operations-banner-track"
              style={{ '--npc-operations-banner-duration': `${getBannerAnimationSeconds(banner)}s` }}
            >
              <strong>{banner.title || t('common.notice', null, '안내')}</strong>
              <span>{banner.message || ''}</span>
            </div>
          </div>
          {banner.dismissible !== false ? (
            <button type="button" onClick={() => dismiss(banner)} aria-label={t('common.noticeClose', null, '공지 닫기')}>×</button>
          ) : null}
        </div>
      ) : null}
      {popup ? (
        <div className="npc-operations-popup-backdrop" role="presentation">
          <section className="npc-operations-popup" role="dialog" aria-modal="true" aria-label={popup.title || t('common.operationsNotice', null, '운영 공지')}>
            <h2>{popup.title || t('common.operationsNotice', null, '운영 공지')}</h2>
            <p>{popup.message || ''}</p>
            {popup.endsAt ? <small>{t('common.scheduledEnd', { time: new Date(popup.endsAt).toLocaleString() }, '종료 예정: {time}')}</small> : null}
            {popup.dismissible !== false ? <button type="button" onClick={() => dismiss(popup)}>{t('common.confirm', null, '확인')}</button> : null}
          </section>
        </div>
      ) : null}
      {system ? (
        <div className="npc-operations-toast" role="status" onClick={() => dismiss(system)}>
          <strong>{system.title || t('common.notice', null, '안내')}</strong>
          <span>{system.message || ''}</span>
        </div>
      ) : null}
    </>
  );

  // App 내부의 stacking context와 분리해 미션/테이블 UI보다 항상 위에 표시한다.
  if (typeof document === 'undefined' || !document.body) return layer;
  return createPortal(layer, document.body);
}
